import { useState } from 'react'

/**
 * AgentAvatar — the animated "ProTrack Agent" owl mascot.
 *
 * Uses /agent-owl.png from frontend/public when present; until that file is
 * saved it falls back to a styled 🦉 emoji so nothing ever looks broken.
 *
 * Animations (pure CSS, injected once):
 *   agent-float — gentle up/down hover, like the owl is flying in place
 *   agent-peek  — occasional curious head-tilt every ~7s
 *   agent-jump  — happy bounce on hover
 *   agent-wave  — soft glow pulse behind the agent
 */

let stylesInjected = false
function injectStyles() {
  if (stylesInjected || typeof document === 'undefined') return
  stylesInjected = true
  const style = document.createElement('style')
  style.textContent = `
    @keyframes agent-float {
      0%, 100% { transform: translateY(0px); }
      50%      { transform: translateY(-7px); }
    }
    @keyframes agent-peek {
      0%, 88%, 100% { transform: rotate(0deg); }
      90%           { transform: rotate(-8deg); }
      94%           { transform: rotate(6deg); }
      97%           { transform: rotate(-3deg); }
    }
    @keyframes agent-glow {
      0%, 100% { box-shadow: 0 0 18px 2px rgba(99,102,241,0.35); }
      50%      { box-shadow: 0 0 26px 6px rgba(139,92,246,0.45); }
    }
    @keyframes agent-jump {
      0%   { transform: translateY(0) scale(1); }
      35%  { transform: translateY(-12px) scale(1.06); }
      60%  { transform: translateY(0) scale(0.97); }
      80%  { transform: translateY(-4px) scale(1.02); }
      100% { transform: translateY(0) scale(1); }
    }
    @keyframes bubble-pop {
      0%   { opacity: 0; transform: translateY(8px) scale(0.9); }
      100% { opacity: 1; transform: translateY(0) scale(1); }
    }
    .agent-float { animation: agent-float 3.2s ease-in-out infinite; }
    .agent-peek  { animation: agent-peek 7s ease-in-out infinite; }
    .agent-glow  { animation: agent-glow 3.2s ease-in-out infinite; }
    .agent-hover-jump:hover { animation: agent-jump 0.7s ease; }
    .agent-bubble { animation: bubble-pop 0.35s ease-out; }
  `
  document.head.appendChild(style)
}

/**
 * Media priority (drop files into frontend/public — no code changes needed):
 *   1. /agent-owl-{state}.webm — TRANSPARENT character video (floats freely, no circle)
 *   2. /agent-owl-idle.webm    — transparent idle video for any missing state
 *   3. /agent-owl-idle.mp4     — opaque video (masked into a circle)
 *   4. /agent-owl.png          — static image with CSS float/peek
 *   5. 🦉 emoji                 — last resort
 *
 * `state` prop: 'idle' | 'greeting' | 'thinking'
 */
export default function AgentAvatar({ size = 48, animated = true, className = '', state = 'idle' }) {
  injectStyles()
  // Source attempt order — transparent webm first, then opaque mp4
  const sources = [
    ...(state !== 'idle' ? [{ src: `/agent-owl-${state}.webm`, transparent: true }] : []),
    { src: '/agent-owl-idle.webm', transparent: true },
    { src: '/agent-owl-idle.mp4', transparent: false },
  ]
  const [srcIdx, setSrcIdx] = useState(0)
  const [imgFailed, setImgFailed] = useState(false)
  const current = sources[srcIdx]

  const anim = animated ? 'agent-float agent-hover-jump' : ''

  return (
    <div
      className={`relative inline-flex items-center justify-center select-none ${anim} ${className}`}
      style={{ width: size, height: size }}
    >
      {current ? (
        // Real animated character — transparent WebM floats freely;
        // opaque MP4 is masked into a circle so its background looks clean.
        <video
          key={current.src}
          src={current.src}
          autoPlay
          loop
          muted
          playsInline
          draggable={false}
          onError={() => setSrcIdx(i => i + 1)}
          className={current.transparent ? '' : 'rounded-full'}
          style={{ width: size, height: size, objectFit: current.transparent ? 'contain' : 'cover' }}
        />
      ) : !imgFailed ? (
        <img
          src="/agent-owl.png"
          alt="ProTrack Agent"
          draggable={false}
          onError={() => setImgFailed(true)}
          className={animated ? 'agent-peek' : ''}
          style={{ width: size, height: size, objectFit: 'contain' }}
        />
      ) : (
        <div
          className={`rounded-full bg-linear-to-br from-indigo-500 to-purple-600 flex items-center justify-center ${animated ? 'agent-glow agent-peek' : ''}`}
          style={{ width: size, height: size, fontSize: size * 0.55 }}
        >
          <span role="img" aria-label="ProTrack Agent">🦉</span>
        </div>
      )}
    </div>
  )
}
